import React, { useState } from 'react';
import { Download, User, Phone, Loader2, CheckCircle2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrochureModal({ open, onOpenChange }) {
  const { lang } = useLanguage();
  const t = translations[lang].brochure;
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError(t.errorRequired);
      return;
    }
    if (!/^[6-9]\d{9}$/.test(phone.replace(/\s/g, ''))) {
      setError(t.errorPhone);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          message: 'Brochure download request',
          source: 'brochure_modal',
        }),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(t.errorGeneric);
      }
    } catch {
      setError(t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSuccess(false);
      setName('');
      setPhone('');
      setError('');
    }, 300);
  };

  const whatsappUrl = `https://wa.me/919403890991?text=${encodeURIComponent(`Hi, I am ${name}. Please send me the Lakshmi Nivasa brochure and pricing details. My phone: ${phone}`)}`;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="brochure-modal"
        className="sm:max-w-md bg-[#FDFBF7] border-[#E5DFD3] rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#1A2520]">
            {success ? t.successTitle : t.title}
          </DialogTitle>
          <DialogDescription className="text-[#5C6A61] text-sm">
            {success ? t.successDesc : t.subtitle}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-4 text-center space-y-4" data-testid="brochure-success">
            <CheckCircle2 className="w-12 h-12 text-[#2C4C3B] mx-auto" />
            <p className="text-sm text-[#5C6A61]">{t.successMsg}</p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brochure-whatsapp-link"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BD5B] text-white px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200"
            >
              <Download className="w-4 h-4" />
              {t.getViaWhatsApp}
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-[#1A2520] text-xs font-medium flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-[#5C6A61]" />
                {t.nameLabel}
              </label>
              <Input
                data-testid="brochure-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.namePlaceholder}
                className="border-[#E5DFD3] focus-visible:ring-[#2C4C3B] h-11"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[#1A2520] text-xs font-medium flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-[#5C6A61]" />
                {t.phoneLabel}
              </label>
              <Input
                data-testid="brochure-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t.phonePlaceholder}
                type="tel"
                className="border-[#E5DFD3] focus-visible:ring-[#2C4C3B] h-11"
              />
            </div>
            {error && <p data-testid="brochure-error" className="text-red-500 text-xs">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="brochure-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#2C4C3B] hover:bg-[#213A2D] disabled:opacity-60 text-white py-3 rounded-xl text-sm font-semibold transition-all duration-200"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {loading ? t.submitting : t.submitBtn}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
